import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CloudUpload, Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AGENT_PROMPT, SPEECH_SETTINGS } from "@/lib/agentConfig";

export const VapiSync = () => {
  const { user } = useAuth();
  const [assistants, setAssistants] = useState(null);
  const [selected, setSelected] = useState("");
  const [lastSynced, setLastSynced] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/vapi/assistants")
      .then(({ data }) => {
        setAssistants(data.assistants);
        setLastSynced(data.last_synced_at);
        const withPhone = data.assistants.find((a) => a.phone_numbers.length > 0);
        setSelected(data.selected_assistant_id || withPhone?.id || data.assistants[0]?.id || "");
      })
      .catch((err) => setLoadError(formatApiErrorDetail(err.response?.data?.detail) || "Could not load your Vapi assistants"));
  }, [isAdmin]);

  if (!isAdmin) return null;

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/vapi/sync", {
        assistant_id: selected,
        system_prompt: AGENT_PROMPT,
        speech_settings: JSON.parse(SPEECH_SETTINGS),
      });
      setLastSynced(data.last_synced_at);
      toast.success(`"${data.assistant_name}" updated — prompt and anti-interruption settings applied`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="border border-[#007AFF]/40 bg-[#007AFF]/5 p-6 mt-8" data-testid="vapi-sync-panel">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#007AFF] mb-3 flex items-center gap-2">
        <CloudUpload className="w-4 h-4" /> One-click sync to Vapi
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Push the recommended system prompt and anti-interruption speech settings
        directly to your Vapi agent.
      </p>
      {loadError ? (
        <p className="text-sm text-red-400" data-testid="vapi-sync-error">{loadError}</p>
      ) : !assistants ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="vapi-sync-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your Vapi assistants…
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="vapi-assistant-select" className="sm:w-96 rounded-sm bg-[#0A0A0A] border-border">
              <SelectValue placeholder="Choose an assistant" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-border rounded-sm">
              {assistants.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  <span className="flex items-center gap-2">
                    {a.name}
                    {a.phone_numbers.length > 0 && (
                      <span className="text-[#007AFF] text-xs font-mono flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {a.phone_numbers.join(", ")}
                      </span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={sync} disabled={!selected || syncing} data-testid="vapi-sync-btn"
            className="rounded-sm gap-2 bg-[#007AFF] hover:bg-[#0066d6] text-white font-semibold">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />}
            {syncing ? "Syncing…" : "Sync settings to this agent"}
          </Button>
        </div>
      )}
      {lastSynced && (
        <p className="text-[11px] text-muted-foreground font-mono mt-3" data-testid="vapi-last-synced">
          Last synced {new Date(lastSynced).toLocaleString()}
        </p>
      )}
    </div>
  );
};
